/**
 * Client Data Transformers
 *
 * Centralizes response transformations between unified API (camelCase)
 * and frontend expectations (snake_case) for backward compatibility.
 */

/**
 * Transforms a single address from unified API (camelCase) to frontend (snake_case)
 * @param {Object} address - Address object from unified API
 * @returns {Object} Transformed address with snake_case fields
 */
export const transformAddressResponse = address => {
  if (!address) return null;

  return {
    id: address.id,
    type: address.type,
    address_1: address.address1 || address.address_1,
    address_2: address.address2 || address.address_2,
    city: address.city,
    state: address.state,
    postal_code: address.postalCode || address.postal_code,
    country: address.country,
  };
};

/**
 * Transforms array of addresses from unified API
 * @param {Array} addresses - Array of addresses from unified API
 * @returns {Array} Transformed addresses array
 */
export const transformAddressesResponse = addresses => {
  if (!Array.isArray(addresses)) return [];
  return addresses.map(transformAddressResponse);
};

/**
 * Transforms unified API client response to frontend expected format
 * @param {Object} apiResponse - Response from GET /api/v1/clients/{clientId}
 * @returns {Object} Transformed client data with snake_case fields
 */
export const transformClientResponse = apiResponse => {
  if (!apiResponse) return null;

  return {
    ...apiResponse,
    // Map camelCase to snake_case for backward compatibility
    client_id: apiResponse.id,
    preferred_name: apiResponse.preferredName || apiResponse.preferred_name || null,
    preferred_contact_type: apiResponse.preferredContactType || apiResponse.preferred_contact_type,
    do_not_contact: apiResponse.doNotContact ?? apiResponse.do_not_contact,
    pos_ref_num: apiResponse.posRefNum || apiResponse.pos_ref_num,
    associate_id: apiResponse.associateId || apiResponse.associate_id || null,
    store_id: apiResponse.storeId || apiResponse.store_id,
    mobile_invalid: apiResponse.mobileInvalid ?? apiResponse.mobile_invalid,
    mobile_validated: apiResponse.mobileValidated ?? apiResponse.mobile_validated,
    opt_in_date: apiResponse.optInDate || apiResponse.opt_in_date || null,
    opt_out_date: apiResponse.optOutDate || apiResponse.opt_out_date || null,
    opt_in_req_date: apiResponse.optInReqDate || apiResponse.opt_in_req_date || null,
    // Transform addresses from camelCase to snake_case
    addresses: transformAddressesResponse(apiResponse.addresses),
  };
};

/**
 * Transforms unified API tags response to frontend expected format
 * @param {Array} tags - Array of tags from GET /api/v1/clients/{clientId}/tags
 * @returns {Object} Object with details_tag_list property
 */
export const transformTagsResponse = tags => ({
  details_tag_list: tags || [],
});

/**
 * Transforms unified API relationship response to frontend expected format
 * @param {Object} relationship - Single relationship from unified API
 * @returns {Object} Transformed relationship with snake_case fields
 */
export const transformRelationshipResponse = relationship => {
  if (!relationship) return null;

  return {
    id: relationship.id,
    client_id: relationship.client?.id || relationship.clientId || relationship.client_id,
    client_name: relationship.client?.name || relationship.clientName || relationship.client_name,
    related_client_id: relationship.relatedClient?.id || relationship.relatedClientId || relationship.related_client_id,
    related_client_name: relationship.relatedClient?.name || relationship.relatedClientName || relationship.related_client_name,
    relationship_type_id: relationship.relationshipType?.id || relationship.relationshipTypeId || relationship.relationship_type_id,
    relationship: relationship.relationshipType?.name || relationship.relationshipTypeName || relationship.relationship,
  };
};

/**
 * Transforms array of relationships from unified API
 * @param {Array} relationships - Array from GET /api/v1/clients/{clientId}/relationships
 * @returns {Array} Transformed relationships array
 */
export const transformRelationshipsResponse = relationships => {
  const data = Array.isArray(relationships) ? relationships : relationships?.data;
  if (!Array.isArray(data)) return [];
  return data.map(transformRelationshipResponse);
};

/**
 * Builds mixed relationship object for UI display
 * Combines all relationship types with client's actual relationships
 * @param {number} clientId - Current client ID
 * @param {Array} relationships - Client's relationships
 * @param {Array} allRelationshipTypes - All available relationship types
 * @returns {Object} Mixed relationship object for UI
 */
export const buildMixedRelationships = (clientId, relationships = [], allRelationshipTypes = []) => {
  const mixedRelationshipObj = {};

  allRelationshipTypes.forEach(relType => {
    mixedRelationshipObj[relType.id] = {
      originalId: null,
      client_id: null,
      client_name: null,
      related_client_name: null,
      relationship_type_id: relType.id,
      related_client_id: null,
      relationship: relType.name,
      relatedTo: false,
    };

    relationships.forEach(clientRel => {
      const relTypeId = clientRel.relationship_type_id || clientRel.relationshipType?.id || clientRel.relationshipTypeId;

      if (relType.id === relTypeId) {
        const relatedClientId = clientRel.related_client_id || clientRel.relatedClient?.id || clientRel.relatedClientId;
        mixedRelationshipObj[relType.id] = {
          originalId: clientRel.id,
          client_id: clientRel.client_id || clientRel.client?.id || clientRel.clientId,
          client_name: clientRel.client_name || clientRel.client?.name || clientRel.clientName,
          related_client_name: clientRel.related_client_name || clientRel.relatedClient?.name || clientRel.relatedClientName,
          relationship_type_id: relTypeId,
          related_client_id: relatedClientId,
          relationship: clientRel.relationship || clientRel.relationshipType?.name || clientRel.relationshipTypeName,
          relatedTo: clientId == relatedClientId,
        };
      }

      // Handle relationships without a type
      if (!relTypeId) {
        const relatedClientId = clientRel.related_client_id || clientRel.relatedClient?.id || clientRel.relatedClientId;
        mixedRelationshipObj[clientRel.id] = {
          originalId: clientRel.id,
          client_id: clientRel.client_id || clientRel.client?.id || clientRel.clientId,
          client_name: clientRel.client_name || clientRel.client?.name || clientRel.clientName,
          related_client_name: clientRel.related_client_name || clientRel.relatedClient?.name || clientRel.relatedClientName,
          relationship_type_id: relTypeId,
          related_client_id: relatedClientId,
          relationship: clientRel.relationship || clientRel.relationshipType?.name || clientRel.relationshipTypeName,
          relatedTo: clientId == relatedClientId,
          uniqueRel: true,
        };
      }
    });
  });

  return mixedRelationshipObj;
};
